import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { globalStyles } from "../../../styles/GlobalStyles";
import formatDateTime from "../../../helpers/formatDateTime";
import { useTranslation } from "react-i18next";
import {
  VOICE_NOTE_CLASSIFICATON,
  VOICE_NOTE_TIMELINE,
} from "../types/CommonTypes";
import Icons from "../../../assets/Icons";
import { toggleEditVoiceNoteDrawer } from "./EditVoiceNoteDrawer";
import NoteChip from "./NoteChip";
import { toggleRevisedCaseNoteDrawer } from "./RevisedCaseNoteDrawer";

const VoiceNoteTimeline = ({
  item,
  hideLine = false,
  isNotLast = false,
  isEditMode = false,
  timelineItemIndex,
}: {
  item: VOICE_NOTE_TIMELINE;
  hideLine?: boolean;
  isNotLast?: boolean;
  isEditMode?: boolean;
  timelineItemIndex: number;
}) => {
  const { t } = useTranslation();
  const { note, timestamp, loggedBy, classifications, caseId, revisionLogs } = item;

  const enabledClassifications = useMemo(
    () =>
      classifications?.filter(
        (classification: VOICE_NOTE_CLASSIFICATON) => classification.isEnabled
      ) ?? [],
    [classifications]
  );

  const handleVoiceNoteClick = () => {
    if (isEditMode) {
      toggleEditVoiceNoteDrawer({
        caseId: caseId ? parseInt(caseId) : undefined,
        voiceNote: item,
        timelineItemIndex: timelineItemIndex,
      });
    }
  };

  const revisionCount = useMemo(() => {
    return (
      revisionLogs?.filter((log) => log?.action === "UPDATE").length ?? 0
    );
  }, [revisionLogs]);

  const handleRevisionClick = () => {
    if (revisionCount <= 0) {
      return;
    }

    toggleRevisedCaseNoteDrawer({
      note,
      revisionLogs,
    });
  };

  return (
  <Box sx={globalStyles.row}>
    {!hideLine && (
      <Box sx={styles.timelineColumn}>
        <Icons.VoiceNote width={24} height={24} />

        {isNotLast && <Box sx={styles.line} />}
      </Box>
    )}

    <Box
      sx={[
        styles.voiceNoteCard,
        !hideLine && isNotLast && styles.cardSpacing,
        hideLine && styles.compactCard,
      ]}
    >
      <Typography sx={styles.title}>
        {t("Voice_Note")}
      </Typography>

      <Typography
        onClick={handleVoiceNoteClick}
        sx={[
          styles.noteText,
          isEditMode && styles.editModeTextBox,
        ]}
      >
        {note}
      </Typography>

      <Typography sx={styles.metaText}>
        {t("Logged_at_and_by", {
          logged_at: formatDateTime(
            timestamp,
            true,
            "hh:mm:ss aaa",
          ),
          logged_by: loggedBy,
        })}
      </Typography>

      {revisionCount > 0 && (
        <Box
          onClick={handleRevisionClick}
          sx={styles.revisionChip}
        >
          <Typography sx={styles.revisionChipText}>
            {revisionCount === 1
              ? t("revision_logged_count", {
                  count: revisionCount,
                })
              : t("revisions_logged_count", {
                  count: revisionCount,
                })}
          </Typography>
        </Box>
      )}

      {enabledClassifications.length > 0 && (
        <Box sx={styles.classificationContainer}>
          {enabledClassifications.map(
            (classification: VOICE_NOTE_CLASSIFICATON) => (
              <NoteChip
                key={classification.type}
                icon={<Icons.AiGenerated />}
                label={classification.type}
                sx={{
                  backgroundColor: classification.colorCode,
                }}
              />
            ),
          )}
        </Box>
      )}
    </Box>
  </Box>
);
};

const styles = {
  timelineColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mr: "12px",
  },

  line: {
    width: "4px",
    flex: 1,
    borderRadius: "4px",
    backgroundColor: "custom.background.activity",
  },

  voiceNoteCard: {
    flex: 1,
    minWidth: 0,
    minHeight: "88px",

    backgroundColor: "custom.background.primary",

    border: "1px solid #E5E7EB",
    borderRadius: "8px",

    px: "12px",
    py: "10px",

    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },

  cardSpacing: {
    mb: "16px",
  },

  compactCard: {
    width: "100%",
  },

  title: {
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 700,
    color: "custom.foreground.primary",
  },

  noteText: {
    mt: "3px",

    fontSize: "15px",
    lineHeight: "20px",
    fontWeight: 400,

    color: "custom.foreground.primary",

    border: "1px solid transparent",
    borderRadius: "4px",

    width: "fit-content",
    maxWidth: "100%",

    px: "3px",
    ml: "-3px",

    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  editModeTextBox: {
    borderColor: "#79747E",
    cursor: "pointer",

    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },

  metaText: {
    mt: "3px",

    fontSize: "12px",
    lineHeight: "16px",

    color: "custom.foreground.secondary",
  },

  revisionChip: {
    display: "flex",
    alignItems: "center",

    width: "fit-content",

    mt: "6px",

    border: "1px solid #84ADFF",
    borderRadius: "4px",

    px: "6px",
    py: "3px",

    cursor: "pointer",

    backgroundColor: "#FFFFFF",
  },

  revisionChipText: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    color: "#155EEF",
  },

  classificationContainer: {
    mt: "6px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
};

export default VoiceNoteTimeline;
